import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, type Transaction } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import type { GrammarApiSuggestion } from '../../types/grammar';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    grammarErrors: {
      setSuggestions: (suggestions: GrammarApiSuggestion[]) => ReturnType;
      setFocusedId: (id: string | null) => ReturnType;
    };
  }
}

const GrammarPluginKey = new PluginKey('grammarErrors');

type PluginState = {
  suggestions: GrammarApiSuggestion[];
  focusedId: string | null;
};

export const GrammarErrors = Extension.create({
  name: 'grammarErrors',
  addOptions() {
    return {
      suggestions: [] as GrammarApiSuggestion[],
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<PluginState>({
        key: GrammarPluginKey,
        state: {
          init() {
            return { suggestions: [], focusedId: null };
          },
          apply(tr, value) {
            const meta = tr.getMeta(GrammarPluginKey) as any;
            if (meta?.type === 'setSuggestions') {
              return { ...value, suggestions: meta.suggestions || [] };
            }
            if (meta?.type === 'setFocusedId') {
              return { ...value, focusedId: meta.id ?? null };
            }
            return value;
          },
        },
        props: {
          decorations(state) {
            const pluginState = GrammarPluginKey.getState(state) as PluginState | undefined;
            if (!pluginState || !pluginState.suggestions?.length) return null;
            const decos: Decoration[] = [];
            const focusedId = pluginState.focusedId ?? null;
            for (const s of pluginState.suggestions) {
              if (typeof s.start === 'number' && typeof s.end === 'number' && s.start < s.end) {
                const isFocused = s.id === focusedId;
                const className = isFocused ? 'grammar-error-line grammar-error-focus' : 'grammar-error-line';
                decos.push(
                  Decoration.inline(s.start, s.end, {
                    class: className,
                    'data-tooltip': s.reason_tamil ?? '',
                    'data-id': s.id,
                  } as any)
                );
              }
            }
            return DecorationSet.create(state.doc, decos);
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      setSuggestions:
        (suggestions: GrammarApiSuggestion[]) =>
        ({ tr }: { tr: Transaction }) => {
          tr.setMeta(GrammarPluginKey, { type: 'setSuggestions', suggestions });
          return true;
        },
      setFocusedId:
        (id: string | null) =>
        ({ tr }: { tr: Transaction }) => {
          tr.setMeta(GrammarPluginKey, { type: 'setFocusedId', id });
          return true;
        },
    };
  },
});

export default GrammarErrors;
